import React from 'react';
import { twMerge } from 'utilities/src/twMerge';

import { ThemeEnum } from 'ui/src/components/Theme';

import MatchedMap from 'utilities/src/matchedMap';
import type { FooterMenu } from '../../data/FooterMenuType';

import useFooterThemes from './themes';
import MenuOptions from './MenuOptions';
import LegalCopyRight from './LegalCopyright';
import GetInTouch, { type GetInTouchProps } from './GetInTouch';
import SubscriptionFooter, { type SubscriptionFooterProps } from './SuscriptionFooter';
import SocialLinks from './SocialLinks';
import LogoFooter from './LogoFooter';
import FooterBGEnum from './FooterBGEnum';

export type FooterButtonsProps<Theme extends ThemeEnum = ThemeEnum> = {
  scheduleCallBtn: GetInTouchProps<Theme>['scheduleCallBtn'];
  contactUsBtn: GetInTouchProps<Theme>['contactUsBtn'];
  subscriptionBtn: SubscriptionFooterProps<Theme>['subscriptionBtn'];
};

export { FooterBGEnum };

type BairesDevFooterProps<Theme extends ThemeEnum = ThemeEnum> = {
  menus?: FooterMenu[];
  logoPath: string;
  logoSize: [number, number];
  subscriptionApi: string;
  hasSocialTitle?: boolean;
  buttonsProps: FooterButtonsProps<Theme>;
  withFullStops?: boolean;
  year?: number;
  contactusUrl?: string;
  bgFooter?: FooterBGEnum;
  label?: string;
  placeholder?: string;
  csrfToken?: string;
};

function BairesDevFooter<Theme extends ThemeEnum = ThemeEnum.Blog>({
  menus,
  logoPath,
  logoSize,
  subscriptionApi,
  hasSocialTitle,
  buttonsProps,
  withFullStops,
  year,
  contactusUrl = 'https://www.bairesdev.com/contact-us/',
  bgFooter,
  label,
  placeholder = 'Your e-mail address',
  csrfToken,
}: BairesDevFooterProps<Theme>) {
  const {
    footerClasses,
    footerBackgroundClassesDark,
    footerBackgroundClassesLight,
    footerBackgroundClasses,
    footerInnerClasses,
    footerGridMenuClasses,
    footerMenuListContainerClasses,
    footerCommonGridMenuClasses,
    footerDivider,
  } = useFooterThemes();

  const bgFooterClassesMap = new MatchedMap([
    [FooterBGEnum.Dark, footerBackgroundClassesDark],
    [FooterBGEnum.Light, footerBackgroundClassesLight],
  ]);

  return (
    <footer className={twMerge(footerClasses, footerBackgroundClasses, bgFooterClassesMap.get(bgFooter!))}>
      <div className={twMerge(footerInnerClasses)}>
        <LogoFooter logoPath={logoPath} logoSize={logoSize} />
        <section className={twMerge(footerCommonGridMenuClasses, footerGridMenuClasses)}>
          {menus &&
            menus.map((menu, index) => (
              <MenuOptions
                key={menu.title}
                classes={twMerge(footerMenuListContainerClasses[index])}
                menu={menu}
                withFullStops={withFullStops}
              />
            ))}
        </section>

        <GetInTouch<Theme>
          scheduleCallBtn={buttonsProps.scheduleCallBtn}
          contactUsBtn={buttonsProps.contactUsBtn}
          withFullStops={withFullStops}
          contactusUrl={contactusUrl}
        />
        <SubscriptionFooter<Theme>
          subscriptionApi={subscriptionApi}
          subscriptionBtn={buttonsProps.subscriptionBtn}
          label={label}
          placeholder={placeholder}
          csrfToken={csrfToken}
        />
        <SocialLinks hasTitle={hasSocialTitle} withFullStops={withFullStops} />
        <div className={twMerge(footerDivider)} />
        <LegalCopyRight yearData={year} />
      </div>
    </footer>
  );
}

export default BairesDevFooter;
